import {
  Box,
  Grid,
  HStack,
  Icon,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { useHits, useInstantSearch } from "react-instantsearch";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { FiChevronRight } from "react-icons/fi";
import type { AlgoliaAdCampaignObject } from "common/interfaces/Algolia";
import { PERFORMANCE_TIER_COLOR_SCHEME } from "./utils";

dayjs.extend(relativeTime);

export type CampaignsTableHitsProps = {
  /** When false, the status/performance dot is not shown (e.g. Past Campaigns table). */
  showPerformanceIndicator?: boolean;
  /** When true, subtitle shows "Ended today" / "Ended X days ago" instead of launched (e.g. Past Campaigns table). */
  isPastCampaigns?: boolean;
};

/** Campaign name takes remaining space (1fr); metric columns fixed and right-aligned. Labels are in each row. Budget column wide enough for "spent / total" (e.g. $1,234.56 / $1,500.00). */
const TABLE_GRID_TEMPLATE = "1fr 160px 80px 90px 200px";
const GRID_GAP = 6;

function MetricCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <VStack align="flex-end" spacing={0} minW={0}>
      <Text
        fontSize="xs"
        color="gray.500"
        textTransform="uppercase"
        fontWeight="600"
      >
        {label}
      </Text>
      <Text
        noOfLines={1}
        color="#1A202C"
        fontSize="1rem"
        fontStyle="normal"
        fontWeight={400}
        lineHeight="100%"
      >
        {children}
      </Text>
    </VStack>
  );
}

function InsightsCellContent({ row }: { row: AlgoliaAdCampaignObject }) {
  const summaries = row.intent_summaries ?? [];
  const primary = row.primary_intent_summary;
  const hasSummaries = summaries.length > 0;
  const fallbackPrimary = primary != null && primary !== "" ? primary : null;

  if (hasSummaries) {
    if (summaries.length === 1) {
      return (
        <Text
          noOfLines={1}
          overflow="hidden"
          textOverflow="ellipsis"
          minW={0}
          color="gray.600"
          fontSize="0.875rem"
          fontStyle="normal"
          fontWeight={600}
          lineHeight="130%"
        >
          {summaries[0]}
        </Text>
      );
    }
    const label = `${summaries.length} Optimizations`;
    return (
      <Tooltip
        label={
          <VStack align="stretch" spacing={1} py={1}>
            {summaries.map((s, i) => (
              <Text key={i} fontSize="sm" whiteSpace="normal">
                {s}
              </Text>
            ))}
          </VStack>
        }
        hasArrow
        placement="top"
        maxW="320px"
      >
        <Text
          noOfLines={1}
          overflow="hidden"
          textOverflow="ellipsis"
          minW={0}
          cursor="default"
          color="gray.600"
          fontSize="0.875rem"
          fontStyle="normal"
          fontWeight={600}
          lineHeight="130%"
        >
          {label}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Text
      noOfLines={1}
      overflow="hidden"
      textOverflow="ellipsis"
      minW={0}
      color="gray.600"
      fontSize="0.875rem"
      fontStyle="normal"
      fontWeight={600}
      lineHeight="130%"
    >
      {fallbackPrimary ?? "—"}
    </Text>
  );
}

function campaignSubtitle(
  row: AlgoliaAdCampaignObject,
  isPastCampaigns?: boolean
): string {
  const now = dayjs();
  const start = row.schedule_start_at ? dayjs(row.schedule_start_at) : null;
  const end = row.schedule_end_at ? dayjs(row.schedule_end_at) : null;
  const isPublished = row.status === "ACTIVE";

  if (isPastCampaigns && end?.isValid()) {
    const todayEnd = now.startOf("day");
    const endDay = end.startOf("day");
    const daysAgo = todayEnd.diff(endDay, "day");
    if (daysAgo === 0) return "Ended today";
    if (daysAgo === 1) return "Ended 1 day ago";
    return `Ended ${daysAgo} days ago`;
  }

  if (!isPastCampaigns && !isPublished) {
    if (row.status === "PAUSED") return "Paused";
    if (row.status === "FROZEN") return "Frozen";
    if (row.status && row.status.trim().length > 0) {
      const prettyStatus = row.status
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
      return prettyStatus;
    }
    return "Not published";
  }

  const parts: string[] = [];
  if (start?.isValid()) {
    const todayStart = now.startOf("day");
    const startDay = start.startOf("day");
    const daysAgo = todayStart.diff(startDay, "day");
    if (daysAgo < 0) {
      const daysUntil = -daysAgo;
      parts.push(
        daysUntil === 1 ? "Starts tomorrow" : `Starts in ${daysUntil} days`
      );
    } else if (daysAgo === 0) {
      parts.push("Launched today");
    } else {
      parts.push(`Launched ${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`);
    }
  }
  if (end?.isValid() && end.isAfter(now)) {
    const daysLeft = end.diff(now, "day");
    parts.push(`${daysLeft} day${daysLeft === 1 ? "" : "s"} left`);
  } else if (end?.isValid() && end.isBefore(now)) {
    parts.push("Ended");
  } else if (!end?.isValid() && start?.isValid()) {
    parts.push("No end date");
  }
  return parts.length > 0 ? parts.join(" • ") : "—";
}

export default function CampaignsTableHits({
  showPerformanceIndicator = true,
  isPastCampaigns = false,
}: CampaignsTableHitsProps = {}) {
  const { status } = useInstantSearch();
  const { items: hits } = useHits<AlgoliaAdCampaignObject>();

  if (status === "loading") {
    return (
      <VStack py={6}>
        <Spinner />
      </VStack>
    );
  }

  if (!hits.length) {
    return (
      <Text color="gray.500" py={4} px={4}>
        No campaigns match your search.
      </Text>
    );
  }

  return (
    <Box minW="min-content">
      <VStack
        align="stretch"
        spacing={0}
        divider={<Box h="1px" bg="gray.100" />}
        minW={{ base: "640px", md: "auto" }}
      >
        {hits.map((row) => {
          // Dot color from performance_tier; same tokens as legend (CampaignsTableHeader LEGEND_ITEMS).
          const dotColor =
            PERFORMANCE_TIER_COLOR_SCHEME[row.performance_tier ?? ""] ??
            "gray.400";
          const subtitle = campaignSubtitle(row, isPastCampaigns);
          return (
            <Link key={row.objectID} to={`/campaigns/ads/${row.objectID}`}>
              <Grid
                px={4}
                py={3}
                alignItems="center"
                gap={GRID_GAP}
                templateColumns={TABLE_GRID_TEMPLATE}
                _hover={{ bg: "gray.50" }}
                minW={0}
              >
                <VStack
                  align="stretch"
                  spacing={0}
                  minW={0}
                  w="100%"
                  overflow="hidden"
                >
                  <HStack align="center" minW={0} gap={2}>
                    {showPerformanceIndicator && (
                      <Box
                        w={2}
                        h={2}
                        borderRadius="full"
                        bg={dotColor}
                        flexShrink={0}
                      />
                    )}
                    <Text
                      fontWeight="600"
                      fontSize="lg"
                      noOfLines={1}
                      minW={0}
                      flex={1}
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {row.name}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.500" noOfLines={1}>
                    {subtitle}
                  </Text>
                </VStack>
                <VStack align="flex-end" spacing={0} minW={0}>
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textTransform="uppercase"
                    fontWeight="600"
                  >
                    Budget spent
                  </Text>
                  <Text
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    minW={0}
                    title={`${row.display_spend ?? "—"}${row.display_budget != null && row.display_budget !== "" ? ` / ${row.display_budget}` : ""}`}
                    color="#1A202C"
                    fontSize="1rem"
                    fontStyle="normal"
                    fontWeight={400}
                    lineHeight="100%"
                  >
                    <Text as="span" color="#1A202C">
                      {row.display_spend ?? "—"}
                    </Text>
                    {row.display_budget != null &&
                      row.display_budget !== "" && (
                        <Text as="span" color="#7B8B9E">
                          {" "}
                          / {row.display_budget}
                        </Text>
                      )}
                  </Text>
                </VStack>
                <MetricCell label="ROAS">{row.display_roas ?? "—"}</MetricCell>
                <MetricCell label="Purchases">
                  {row.display_tickets ?? "—"}
                </MetricCell>
                <VStack align="flex-end" spacing={0} minW={0} overflow="hidden">
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textTransform="uppercase"
                    fontWeight="600"
                  >
                    Insights
                  </Text>
                  <HStack
                    minW={0}
                    overflow="hidden"
                    justify="flex-end"
                    align="center"
                    gap={2}
                  >
                    <InsightsCellContent row={row} />
                    <Icon
                      as={FiChevronRight}
                      color="gray.400"
                      boxSize={4}
                      flexShrink={0}
                    />
                  </HStack>
                </VStack>
              </Grid>
            </Link>
          );
        })}
      </VStack>
    </Box>
  );
}
